import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, TrendingUp, Calendar, House, MousePointerClick } from 'lucide-react';
import { analyticsAPI } from '../../utils/api';

const AdminAnalytics = () => {
  const [pageViews, setPageViews] = useState([]);
  const [stats, setStats] = useState({
    total_views: 0,
    total_page_views: 0,
    total_tab_clicks: 0,
    home_screen_views: 0,
    views_by_project: [],
    views_by_page: [],
    tab_clicks_by_tab: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [viewsRes, statsRes] = await Promise.all([
        analyticsAPI.getPageViews(),
        analyticsAPI.getStats()
      ]);
      setPageViews(viewsRes.data.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBrowserFromUA = (userAgent) => {
    if (!userAgent) return 'Unknown';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Other';
  };

  const getDeviceFromUA = (userAgent) => {
    if (!userAgent) return 'Unknown';
    if (/mobile/i.test(userAgent)) return 'Mobile';
    if (/tablet/i.test(userAgent)) return 'Tablet';
    return 'Desktop';
  };

  const projectEvents = pageViews.filter(
    (view) => view.event_type === 'project_view' || !!view.project_id
  );

  const pageAnalyticsEvents = pageViews.filter(
    (view) => view.event_type !== 'project_view' && !view.project_id
  );

  return (
    <div data-testid="admin-analytics-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-4xl font-bold font-['Syne'] mb-8">Analytics</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-card border border-white/10 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <Eye className="text-primary" size={24} />
              <h3 className="text-lg font-semibold">Total Project Views</h3>
            </div>
            <p className="text-3xl font-bold">{stats.views_by_project.reduce((sum, item) => sum + item.views, 0)}</p>
          </div>

          <div className="bg-card border border-white/10 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <Eye className="text-primary" size={24} />
              <h3 className="text-lg font-semibold">Page Views</h3>
            </div>
            <p className="text-3xl font-bold">{stats.total_page_views}</p>
          </div>

          <div className="bg-card border border-white/10 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <MousePointerClick className="text-primary" size={24} />
              <h3 className="text-lg font-semibold">Tab Clicks</h3>
            </div>
            <p className="text-3xl font-bold">{stats.total_tab_clicks}</p>
          </div>

          <div className="bg-card border border-white/10 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="text-primary" size={24} />
              <h3 className="text-lg font-semibold">Top Project</h3>
            </div>
            <p className="text-xl font-semibold truncate">
              {stats.views_by_project[0]?.project_title || 'N/A'}
            </p>
            <p className="text-sm text-muted-foreground">
              {stats.views_by_project[0]?.views || 0} views
            </p>
          </div>

          <div className="bg-card border border-white/10 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <House className="text-primary" size={24} />
              <h3 className="text-lg font-semibold">Home Visits</h3>
            </div>
            <p className="text-3xl font-bold">{stats.home_screen_views}</p>
          </div>

          <div className="bg-card border border-white/10 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="text-primary" size={24} />
              <h3 className="text-lg font-semibold">Recent Events</h3>
            </div>
            <p className="text-3xl font-bold">{pageViews.length}</p>
            <p className="text-sm text-muted-foreground">Last 100 records</p>
          </div>
        </div>

        {/* Project Analytics */}
        <div className="bg-card border border-white/10 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold font-['Syne'] mb-6">Project Analytics</h2>
          <div className="space-y-4">
            {stats.views_by_project.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium">{item.project_title}</p>
                  <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                    <div
                      className="bg-primary rounded-full h-2 transition-all"
                      style={{
                        width: `${(item.views / stats.views_by_project[0]?.views) * 100}%`
                      }}
                    />
                  </div>
                </div>
                <p className="ml-4 text-lg font-bold text-primary">{item.views}</p>
              </div>
            ))}
            {stats.views_by_project.length === 0 && (
              <p className="text-muted-foreground">No project views yet</p>
            )}
          </div>

          <div className="mt-8 overflow-x-auto">
            <h3 className="text-xl font-semibold mb-4">Recent Project Views</h3>
            {projectEvents.length === 0 ? (
              <p className="text-muted-foreground">No recent project view events</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4">Project</th>
                    <th className="text-left py-3 px-4">IP Address</th>
                    <th className="text-left py-3 px-4">Device</th>
                    <th className="text-left py-3 px-4">Browser</th>
                    <th className="text-left py-3 px-4">Viewed At</th>
                  </tr>
                </thead>
                <tbody>
                  {projectEvents.map((view) => (
                    <tr key={view.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4">
                        <p className="font-medium">{view.project_title || 'Unknown Project'}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm font-mono text-muted-foreground">{view.ip_address || 'N/A'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-muted-foreground">{getDeviceFromUA(view.user_agent)}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-muted-foreground">{getBrowserFromUA(view.user_agent)}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-muted-foreground">{formatDate(view.viewed_at)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Page Analytics */}
        <div className="bg-card border border-white/10 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold font-['Syne'] mb-6">Page Analytics</h2>
          <div className="space-y-3">
            {stats.views_by_page.map((item, index) => (
              <div key={`${item.page_key}-${index}`} className="flex items-center justify-between py-2 border-b border-white/5">
                <p className="font-medium capitalize">{item.page_title || item.page_key}</p>
                <p className="text-primary font-semibold">{item.views}</p>
              </div>
            ))}
            {stats.views_by_page.length === 0 && (
              <p className="text-muted-foreground">No page views yet</p>
            )}
          </div>

          <h3 className="text-xl font-semibold mb-4 mt-8">Tab Clicks</h3>
          <div className="space-y-3">
            {stats.tab_clicks_by_tab.map((item, index) => (
              <div key={`${item.tab_name}-${index}`} className="flex items-center justify-between py-2 border-b border-white/5">
                <p className="font-medium">{item.tab_name}</p>
                <p className="text-primary font-semibold">{item.clicks}</p>
              </div>
            ))}
            {stats.tab_clicks_by_tab.length === 0 && (
              <p className="text-muted-foreground">No tab click data yet</p>
            )}
          </div>

          <div className="mt-8">
            <h3 className="text-xl font-semibold mb-4">Recent Page Events</h3>
            {loading ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Loading analytics...</p>
              </div>
            ) : pageAnalyticsEvents.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No page analytics events yet</p>
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4">Event</th>
                    <th className="text-left py-3 px-4">Page/Tab</th>
                    <th className="text-left py-3 px-4">IP Address</th>
                    <th className="text-left py-3 px-4">Device</th>
                    <th className="text-left py-3 px-4">Browser</th>
                    <th className="text-left py-3 px-4">Viewed At</th>
                  </tr>
                </thead>
                <tbody>
                  {pageAnalyticsEvents.map((view) => (
                    <tr key={view.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4">
                        <p className="font-medium capitalize">{(view.event_type || 'page_view').replace('_', ' ')}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-medium">{view.tab_name || view.page_title || view.page_key || 'N/A'}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm font-mono text-muted-foreground">
                          {view.ip_address || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-muted-foreground">
                          {getDeviceFromUA(view.user_agent)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-muted-foreground">
                          {getBrowserFromUA(view.user_agent)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(view.viewed_at)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminAnalytics;
